import { Row } from "@tanstack/react-table";
import "@types";
import classNames from "classnames";

import { useTableContext } from "../context";
import Select from "../select";
import TableRowCell from "./cell";

interface TableRowsProps<T> {
  rows: Row<T>[];
  selectable: boolean;
}

const getRowRange = <T,>(
  rows: Row<T>[],
  currentID: number,
  selectedID: number,
): Row<T>[] => {
  const rangeStart = selectedID > currentID ? currentID : selectedID;
  const rangeEnd = rangeStart === currentID ? selectedID : currentID;
  return rows.slice(rangeStart, rangeEnd + 1);
};

function TableRows<T>({ rows, selectable }: TableRowsProps<T>) {
  const { table, editMode } = useTableContext();
  const onRowClick = table.options.meta?.onRowClick as
    ((row: Row<T>) => void) | undefined;

  const handleRangeSelection = (row: Row<T>, shouldSelect: boolean) => {
    const selectedRows = table.getSelectedRowModel().rows;

    if (selectedRows.length > 0) {
      const lastSelectedRow = selectedRows[selectedRows.length - 1];
      const { rows: allRows } = table.getRowModel();
      const currentRowIndex = Number(row.id);
      const lastRowIndex = Number(lastSelectedRow.id);

      const rowsToToggle = getRowRange(allRows, currentRowIndex, lastRowIndex);

      rowsToToggle.forEach((_row) => _row.toggleSelected(shouldSelect));
    } else {
      row.toggleSelected(shouldSelect);
    }
  };

  const handleRowClick = (row: Row<T>, event: React.MouseEvent) => {
    if (!selectable) return;

    const isShiftKey = event.shiftKey;
    const target = event.target as HTMLElement;
    const isInteractiveElement = target.closest(
      'a, button, input, select, textarea, label, [role="button"], [type="checkbox"]',
    );

    if (!isInteractiveElement) {
      if (isShiftKey) {
        const shouldSelect = !row.getIsSelected();
        handleRangeSelection(row, shouldSelect);
      } else if (onRowClick) {
        onRowClick?.(row);
      } else {
        row.toggleSelected();
      }
    }
  };

  const handleCheckboxChange = (
    row: Row<T>,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isShiftKey = (event.nativeEvent as MouseEvent).shiftKey;
    const shouldSelect = !row.getIsSelected();

    if (isShiftKey) {
      handleRangeSelection(row, shouldSelect);
    } else {
      row.toggleSelected(shouldSelect);
    }
  };

  return (
    <tbody className="">
      {rows.map((row) => {
        const isSelected = row.getIsSelected();

        return (
          <tr
            key={row.id}
            onClick={
              editMode ? undefined : (event) => handleRowClick(row, event)
            }
            className={classNames({
              "cursor-pointer hover:bg-primary-50 transition-all duration-200 ease-in-out select-none border-b border-b-gray-200 last:border-b-0":
                selectable && !editMode,
              "bg-primary-50 hover:bg-primary-100": isSelected && selectable,
            })}
          >
            {selectable && (
              <td className={classNames("py-2 px-4 w-0")}>
                <Select
                  row
                  checked={row.getIsSelected()}
                  onChange={(event) => handleCheckboxChange(row, event)}
                />
              </td>
            )}
            {row.getVisibleCells().map((cell) => (
              <TableRowCell key={cell.id} row={row} cell={cell} />
            ))}
          </tr>
        );
      })}
    </tbody>
  );
}

export default TableRows;
